import {inject} from 'vue';
import type {PrismicDocument} from '@prismicio/types';
import type {AcquisitionPageDocument, AcquisitionTemplateDocument} from '@prismicio-types';
import {useRoute} from 'vue-router';
import {callWithNuxt, useNuxtApp} from '#app';
import {createError, usePrismicLang, useSpanWrap} from '#imports';
import type {Profile} from '@malt/nuxt-profile-card-module/types';
import {safelyNavigateTo} from '@malt/utils';
import type {LandingPageContentWithHighlightedProfiles, LandingPageContentWithProfiles, SearchByTagRequest, TagSearchResponse} from '@api';
import {Configuration, FetchError, LandingPageSearchApi, TagSearchApi, type FreelancerProfile} from '@api';
import {usePrismicDocument} from '~/composables/content/usePrismicDocument';
import {PRISMIC_CLIENT} from '~/keys';

export type AcquisitionPage = {
    landingPage: Promise<LandingPageContentWithHighlightedProfiles>;
    prismicTemplate?: Promise<AcquisitionTemplateDocument>;
    prismicContent?: Promise<AcquisitionPageDocument>;
};

export type UseAcquisitionPageParams = {
    familyOrCity: string;
    categoryOrCity: string;
    subCategoryIdOrCity: string;
    city: string;
};

export function useAcquisitionPage(routeParams: UseAcquisitionPageParams) {
    const searchApi = new LandingPageSearchApi(new Configuration({basePath: ''}));
    const client = inject(PRISMIC_CLIENT);
    const {lang} = usePrismicLang();
    const {fetchDocument: fetchPrismicTemplate} = usePrismicDocument('acquisitionTemplate');

    async function fetchPrismicDocument(documentType: string, uid?: string): Promise<PrismicDocument | undefined> {
        const getPrismicDocument = () =>
            uid ? client!.getByUID(documentType, uid, {lang: lang.value}) : client!.getSingle(documentType, {lang: lang.value});
        return await useSpanWrap(getPrismicDocument, {httpMethod: 'prismic.get', kind: 'prismic', name: `[prismic] ${documentType}`});
    }

    async function fetchLandingPage(): Promise<LandingPageContentWithProfiles | LandingPageContentWithHighlightedProfiles> {
        try {
            const landingPage = await searchApi.searchLandingPages(routeParams);
            return landingPage;
        } catch (e) {
            throw createError({statusCode: 404, statusMessage: 'Error fetching acquisition page : ' + e});
        }
    }

    return {
        fetchLandingPage,
        fetchPrismicTemplate,
        fetchPrismicDocument,
    };
}

export function useTagPage() {
    const client = inject(PRISMIC_CLIENT);
    const {lang} = usePrismicLang();
    const searchApi = new TagSearchApi(new Configuration({basePath: ''}));

    async function fetchPrismicDocument(documentType: string, uid?: string): Promise<PrismicDocument | undefined> {
        const getPrismicDocument = () =>
            uid ? client!.getByUID(documentType, uid, {lang: lang.value}) : client!.getSingle(documentType, {lang: lang.value});
        return await useSpanWrap(getPrismicDocument, {httpMethod: 'prismic.get', kind: 'prismic', name: `[prismic] ${documentType}`});
    }

    async function fetchLandingPage(searchRequest: SearchByTagRequest): Promise<TagSearchResponse> {
        const nuxtApp = useNuxtApp();
        const route = useRoute();

        try {
            const searchResult = await searchApi.searchByTag(searchRequest);
            return searchResult;
        } catch (e) {
            // MALT-4RNG: if we receive a bad request and we have a page query, we redirect to the first page without query
            if (e instanceof FetchError && e.statusCode === 400 && Object.keys(route.query).length > 0) {
                await callWithNuxt(nuxtApp, async () => {
                    return await safelyNavigateTo({path: route.path}, {redirectCode: 302});
                });
            }
            throw createError({statusCode: 404, statusMessage: 'Error fetching tag page : ' + e});
        }
    }

    return {
        fetchPrismicDocument,
        fetchLandingPage,
    };
}

export function formatProfile(profile: FreelancerProfile): Profile {
    return {
        ...profile,
        portfolio: [],
        skills: profile.skills?.map((skill) => skill.label) ?? [],
        photoUrl: profile.photo?.absoluteUrl,
        price: {
            visibility: profile.price?.visibility,
            formattedValue: profile.price?.value?.formatted,
        },
    } as Profile;
}
